/**
 * "View floor plan" click delegation for the Native Interactive Site Map listing panel.
 *
 * Event delegation over the panel root — floorPlanDetail.plugin.js's content (including the
 * mobile expand button) is re-rendered/torn down as the user navigates between views, so the
 * listener binds once on the stable panel container instead of being re-attached per render.
 * Pure DOM — no Redux, no CSS, no business logic. Mirrors the delegation style of
 * handlers/getNotifiedInteractionHandlers.js.
 *
 * Previously also delegated Floor Plan Detail tab clicks ([data-tab-value], filtered to
 * TAB_LOT_AND_ELEVATION/TAB_FLOOR_PLAN_CONFIG) into a second, independent tracking emission —
 * removed (NHS20-9000 UAT fix) because it duplicated the tab-selected event that
 * plugins/tracking.plugin.js's onDetailTabClicked already emits via the detailTabClicked$ Subject
 * (fed directly from floorPlanDetail.plugin.js's/housingUnitDetail.plugin.js's own tab-switch
 * handler, the code that actually performs the switch).
 *
 * @module nativeInteractiveSiteMap/handlers/floorPlanDetailInteractionHandlers
 */

const VIEW_FLOOR_PLAN_BUTTON_SELECTOR = '[data-image-expand-button]';

/**
 * @returns {{ bind: Function, unbind: Function }}
 */
function createFloorPlanDetailInteractionHandlers() {
  const state = {
    rootElement: null,
    clickHandler: null,
  };

  function unbind() {
    if (state.rootElement && state.clickHandler) {
      state.rootElement.removeEventListener('click', state.clickHandler);
    }
    state.rootElement = null;
    state.clickHandler = null;
  }

  /**
   * @param {HTMLElement} rootElement - the stable panel container to delegate from
   * @param {object} handlers
   * @param {() => void} [handlers.onViewFloorPlanClicked] - called on a "View floor plan" click;
   *   omit to not bind it at all (mobile-only — see plugins/tracking.plugin.js)
   */
  function bind(rootElement, { onViewFloorPlanClicked } = {}) {
    unbind();
    state.rootElement = rootElement;
    state.clickHandler = (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      if (typeof onViewFloorPlanClicked === 'function' && event.target.closest(VIEW_FLOOR_PLAN_BUTTON_SELECTOR)) {
        onViewFloorPlanClicked();
      }
    };
    rootElement.addEventListener('click', state.clickHandler);
  }

  return { bind, unbind };
}

export default createFloorPlanDetailInteractionHandlers;
